import {useCallback, useMemo} from 'react';
import parse from 'html-react-parser';
import {Box, Typography} from '@mui/material';
import {ISelectItem} from '../../../shared/types';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import './StandardReports.scss';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrActionButton from '../../common/arr-action-button/ArrActionButton';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as SaveIcon} from '../../../assets/images/save.svg';
import ExportDataHeader from './reports/data-export/ExportDataHeader';

type HeaderProps = {
  title: string;
  showInfoIcon?: boolean;
  infoModalTitle?: string;
  infoContent?: string[] | string;
  optionsCaption?: string;
  selectedOption: ISelectItem | null;
  onOptionChange?: (value: ISelectItem | null) => void;
  rightActionOptions?: {label: string; id: string}[];
  onRightActionPick?: (value: {label: string; id: string}) => void;
  selectedRightAction?: {label: string; id: string} | null;
  isExportDataReport?: boolean;
  headerSave?: {
    isOpen: boolean;
    toggle: (v: boolean) => void;
    canSave?: boolean;
  };
};
const USER_ACTIVITY_OPTIONS: ISelectItem[] = [
  {value: 'currentLoggedIn', label: 'Currently Logged-In Users'},
  {value: 'loginHistory', label: 'Login History'},
];

const allowedTags = ['p', 'b', 'ul', 'li', 'br'];
const safeParse = (html: string) => {
  return parse(html, {
    replace: (domNode) => {
      // Check if the node is a tag and if its name is in the allowed tags
      if (domNode.type === 'tag' && !allowedTags.includes(domNode.name)) {
        return <></>; // remove disallowed tags
      }
    },
  });
};
const StandardReportHeader: React.FC<
  HeaderProps & {
    rightPanelHeaderConfig?: any;
    isNoData?: boolean;
    isSaveReportInActive?: boolean;
    canSave?: boolean;
    isSavePanelOpen?: boolean;
    setIsSavePanelOpen?: (v: boolean) => void;
  }
> = ({
  title,
  showInfoIcon,
  infoModalTitle,
  infoContent = '',
  rightActionOptions,
  selectedRightAction,
  optionsCaption,
  selectedOption,
  onOptionChange,
  onRightActionPick,
  isExportDataReport = false,
  headerSave,
  rightPanelHeaderConfig,
  isSavePanelOpen = false,
  setIsSavePanelOpen,
}) => {
  /** Handle dropdown selection */
  const handleSelectGroup = useCallback(
    (value: ISelectItem | null) => {
      if (value?.value) onOptionChange?.(value);
    },
    [onOptionChange]
  );

  const merged = {
    isOpen: headerSave?.isOpen ?? isSavePanelOpen,
    canSave: headerSave?.canSave ?? false,
  };
  const saveDisabled = !merged.canSave;

  /** Dropdown for User Activity */
  const userActivityDropdown = useMemo(() => {
    return (
      <ArrActionButton
        data-testid="user-activity-button"
        dataTestId="user-activity-button"
        aria-label="user-activity-button"
        size="medium"
        direction="bottom-right"
        variant="text"
        title={String(selectedOption?.label || 'Select Activity')}
        menuItems={USER_ACTIVITY_OPTIONS.map((item: any) => ({
          id: item.value,
          label: item.label,
        }))}
        onActionPick={(item) => {
          onOptionChange?.({value: item.id, label: item.label as string});
        }}
      />
    );
  }, [selectedOption, onOptionChange]);

  const renderInfoIcon = () => {
    if (showInfoIcon && infoContent) {
      if (isExportDataReport) {
        return <ExportDataHeader />;
      }
      return (
        <ArrTooltipWrapper
          title={infoModalTitle}
          placement="top"
          trigger={
            <InfoIcon
              data-testid="info-icon"
              className="info-icon"
              tabIndex={0}
            />
          }
          dialogClassName="standard-report-info-dialog"
          data-testid="standard-report-info-tooltip"
        >
          {infoContent instanceof Array ? (
            infoContent.map((item) => <>{safeParse(item)}</>)
          ) : (
            <Typography data-testid={`standard-report-header-info`}>
              {infoContent}
            </Typography>
          )}
        </ArrTooltipWrapper>
      );
    } else if (showInfoIcon) {
      return <InfoIcon data-testid="info-icon" className="info-icon" />;
    }
    return null;
  };

  const reportOptionDropdown = useMemo(() => {
    if (!rightActionOptions || rightActionOptions.length === 0) return null;
    return (
      <Box display="flex" className="right-dropdown-option-container">
        {optionsCaption && (
          <Typography
            className="typo-text label1_size caption-label"
            data-testid={`standard-report-header-options-caption`}
          >
            {optionsCaption}
          </Typography>
        )}
        <ArrActionButton
          size="medium"
          direction="bottom-right"
          variant="text"
          autoFocusMenu
          disableAutoFocusItem={false}
          menuItems={rightActionOptions ?? []}
          title={selectedRightAction?.label}
          onActionPick={(item) =>
            onRightActionPick?.({label: item.label as string, id: item.id})
          }
          menuClassName="standard-report-right-action-menu"
          dataTestId="right-option-button"
        />
      </Box>
    );
  }, [
    rightActionOptions,
    optionsCaption,
    selectedRightAction,
    handleSelectGroup,
    onRightActionPick,
  ]);

  return (
    <div
      className="review-results-header"
      data-testid={`standard-report-header-div`}
    >
      <Box display="flex" alignItems="center" className="heading-title">
        <Typography
          className="typo-text headline3_size review-results-title"
          aria-label={title}
          role="header"
          tabIndex={0}
          data-testid={title.replaceAll(' ', '').toLowerCase() + '-header'}
        >
          {title}
        </Typography>

        {renderInfoIcon()}
      </Box>
      {title === 'User Login Activity' && (
        <Box display="flex" alignItems="center">
          <Typography
            className="typo-text label1_size caption-label"
            data-testid={`standard-report-header-only-show`}
          >
            Only Show
          </Typography>
          <Box>{userActivityDropdown}</Box>
          <Typography
            className="typo-text label1_size caption-label"
            data-testid={`standard-report-header-activity`}
          >
            Activity
          </Typography>
        </Box>
      )}
      {reportOptionDropdown}
      {isExportDataReport && rightPanelHeaderConfig?.showSaveIcon && (
        <ArrIconButton
          size="large"
          onClick={() => {
            if (saveDisabled) return;
            if (headerSave?.toggle) {
              headerSave.toggle(!merged.isOpen);
            } else {
              setIsSavePanelOpen && setIsSavePanelOpen(!merged.isOpen);
            }
          }}
          ariaLabel="Save"
          icon={
            <SaveIcon
              fontSize="medium"
              className={`${saveDisabled ? 'inactive-icon' : ''} `}
            />
          }
          shape={saveDisabled ? 'none' : 'default'}
          disabled={saveDisabled}
          testId="standard-report-header-save-icon-button"
        />
      )}
    </div>
  );
};

export default StandardReportHeader;
